import { useEffect, useState } from "react";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import type { GridPreset } from "../../types";

const GRID_OPTIONS: GridPreset[] = [1, 4, 9, 16, 25, 36];
const MIN_SECONDS = 10;
const MAX_SECONDS = 120;

interface Props {
  open: boolean;
  onCancel: () => void;
  onStart: (intervalSec: number, gridSize: GridPreset) => void;
}

export function GuardiaVirtualModal({ open, onCancel, onStart }: Props) {
  const [secondsText, setSecondsText] = useState("30");
  const [gridSize, setGridSize] = useState<GridPreset>(16);

  // Resetear a los valores por defecto cada vez que se abre.
  useEffect(() => {
    if (open) {
      setSecondsText("30");
      setGridSize(16);
    }
  }, [open]);

  const secondsNum = Number(secondsText);
  const secondsValid =
    secondsText.trim() !== "" &&
    Number.isInteger(secondsNum) &&
    secondsNum >= MIN_SECONDS &&
    secondsNum <= MAX_SECONDS;

  const handleSecondsBlur = () => {
    const t = secondsText.trim();
    if (t === "") return;
    const n = Number(t);
    if (!Number.isFinite(n)) return;
    setSecondsText(String(Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, Math.round(n)))));
  };

  const handleStart = () => {
    if (!secondsValid) return;
    onStart(secondsNum, gridSize);
  };

  return (
    <Modal open={open} onClose={onCancel} title="Guardia Virtual" width="sm">
      <div className="space-y-4">
        <p className="text-xs text-muted">
          Recorre automaticamente todas las camaras de todos los equipos en
          ciclos. El recorrido se pausa cuando interactuas con el mouse.
        </p>

        <div>
          <label className="block text-xs font-medium mb-1">
            Segundos por pagina ({MIN_SECONDS}-{MAX_SECONDS})
          </label>
          <input
            type="number"
            min={MIN_SECONDS}
            max={MAX_SECONDS}
            value={secondsText}
            onChange={(e) => setSecondsText(e.target.value)}
            onBlur={handleSecondsBlur}
            className="w-full bg-bg border border-line rounded px-2 py-1 text-sm text-text"
          />
          {!secondsValid && (
            <p className="text-xs text-red-400 mt-1">
              Ingresa un numero entero entre {MIN_SECONDS} y {MAX_SECONDS}.
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium mb-1">Division de la grilla</label>
          <div className="grid grid-cols-3 gap-2">
            {GRID_OPTIONS.map((g) => (
              <Button
                key={g}
                variant={gridSize === g ? "primary" : "secondary"}
                size="sm"
                onClick={() => setGridSize(g)}
              >
                {g}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={handleStart} disabled={!secondsValid}>
            Iniciar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
